import React, { useState, useRef, useEffect } from 'react';
import { VscAdd, VscDatabase, VscChevronRight, VscChecklist, VscFilter, VscCheck } from 'react-icons/vsc';
import { IoFlashOutline, IoCalendarOutline } from 'react-icons/io5';
import { MdCable } from 'react-icons/md';
import FileSidebar from '../files/FileSidebar';
import ChatSidebar from '../chat/ChatSidebar';
import PlanningSidebar from '../planning/PlanningSidebar';
import QuoteSidebar from '../quotes/QuoteSidebar';
import { getMe, updateMyProfile } from '../../api/users';
import './SidePanel.css';

const LOOKUP_NAV = [
  { id: 'switchboards', label: 'Switchboards & Breakers' },
  { id: 'rooms',        label: 'Rooms' },
  { id: 'systems',      label: 'Systems' },
  { id: 'sections',     label: 'Cable Sections' },
  { id: 'cable-types',  label: 'Cable Types' },
  { id: 'emg-groups',   label: 'EMG Stop Groups' },
];

const REPORT_NAV = [
  { id: 'fuse-list',    label: 'Fuse List' },
  { id: 'megger-lists', label: 'Megger Lists' },
  { id: 'load-calc',    label: 'Load Calculation' },
  { id: 'sld',          label: 'Single Line Diagram' },
];

function NavItem({ id, label, Icon, color, active, onClick, onDoubleClick, indent }) {
  return (
    <button
      className={`sp-nav-item${active ? ' active' : ''}${indent ? ' sp-nav-indent' : ''}`}
      onClick={() => onClick(id, label)}
      onDoubleClick={onDoubleClick ? () => onDoubleClick(id, label) : undefined}
    >
      {Icon && <Icon size={14} style={{ color, flexShrink: 0 }} />}
      {label}
    </button>
  );
}

function SidePanel({ activeSection, activeTabType, activeTabId, activeProject, projects, onOpenTab, onPreviewTab, onSelectProject, onOpenProjectInfo, onNewProject, role, socket, currentUser, chatGroups, setChatGroups, activeChatGroupId, onOpenChat, onlineUsers }) {
  const [lookupsOpen, setLookupsOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const timers = useRef({});
  const projTimers = useRef({});

  // Foreman and above see every project, so they can hide the ones they don't follow (stored per user)
  const canChooseProjects = ['admin', 'engineer', 'manager', 'foreman'].includes(role);
  const [hiddenProjects, setHiddenProjects] = useState([]);
  const [choosingProjects, setChoosingProjects] = useState(false);

  useEffect(() => {
    if (!canChooseProjects) return;
    getMe().then(r => setHiddenProjects((r.data.hiddenProjects || []).map(String))).catch(() => {});
  }, [canChooseProjects, currentUser?.id]);

  const toggleHidden = (id) => {
    const next = hiddenProjects.includes(id) ? hiddenProjects.filter(x => x !== id) : [...hiddenProjects, id];
    setHiddenProjects(next);
    updateMyProfile({ hiddenProjects: next }).catch(() => {});
  };

  // Single click → preview (italic, replaceable); double click → sticky
  const handleClick = (id, label) => {
    if (timers.current[id]) return;
    timers.current[id] = setTimeout(() => {
      delete timers.current[id];
      onPreviewTab ? onPreviewTab(id, label) : onOpenTab(id, label);
    }, 220);
  };

  const handleDblClick = (id, label) => {
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
    onOpenTab(id, label);
  };

  if (activeSection === 'ships') {
    // The active project stays in the list even when hidden
    const shownProjects = projects.filter(p => !hiddenProjects.includes(String(p._id)) || activeProject?._id === p._id);
    const hiddenCount = projects.length - shownProjects.length;
    return (
      <div className="side-panel">
        <div className="side-panel-header">
          <span className="side-panel-title">PROJECTS</span>
          {canChooseProjects && (
            <span className="sp-header-actions">
            <button
              className={`btn-icon${choosingProjects ? ' sp-filter-on' : ''}`}
              title={choosingProjects ? 'Done choosing projects' : 'Choose which projects to show'}
              onClick={() => setChoosingProjects(v => !v)}
            >
              {choosingProjects ? <VscCheck size={16} /> : <VscFilter size={16} />}
            </button>
            <button className="btn-icon" title="New Project" onClick={onNewProject}>
              <VscAdd size={16} />
            </button>
            </span>
          )}
        </div>
        {choosingProjects ? (
          <nav className="sp-nav">
            <div className="sp-choose-hint">Tick the projects to show in the list.</div>
            <div className="sp-choose-actions">
              <button className="sp-choose-link" onClick={() => { setHiddenProjects([]); updateMyProfile({ hiddenProjects: [] }).catch(() => {}); }}>Show all</button>
              <button className="sp-choose-link" onClick={() => { const all = projects.map(p => String(p._id)); setHiddenProjects(all); updateMyProfile({ hiddenProjects: all }).catch(() => {}); }}>Hide all</button>
            </div>
            {projects.map(p => (
              <label key={p._id} className="sp-choose-row">
                <input type="checkbox" checked={!hiddenProjects.includes(String(p._id))} onChange={() => toggleHidden(String(p._id))} />
                <span className="sp-project-name">{p.name}</span>
                {p.newbuildingNo && <span className="sp-project-hull">#{String(p.newbuildingNo).padStart(3, '0')}</span>}
              </label>
            ))}
          </nav>
        ) : (
        <nav className="sp-nav">
          {shownProjects.map(p => (
            <div key={p._id} className="sp-project-row">
              <button
                className={`sp-nav-item sp-project-item${activeProject?._id === p._id ? ' active' : ''}`}
                onClick={() => {
                  if (projTimers.current[p._id]) return;
                  projTimers.current[p._id] = setTimeout(() => {
                    delete projTimers.current[p._id];
                    onSelectProject(p);
                  }, 220);
                }}
                onDoubleClick={() => {
                  if (projTimers.current[p._id]) {
                    clearTimeout(projTimers.current[p._id]);
                    delete projTimers.current[p._id];
                  }
                  onSelectProject(p);
                  onOpenProjectInfo(p);
                }}
              >
                <span className="sp-project-name">
                  {p.name}
                  {['red', 'yellow', 'blue'].map(sev => {
                    const texts = (p.notices || []).filter(n => n.severity === sev).map(n => n.text);
                    return texts.length > 0 && (
                      <span key={sev} className={`sp-notice sp-notice-${sev}`} title={texts.map(t => `• ${t}`).join('\n')}>!</span>
                    );
                  })}
                </span>
                {p.newbuildingNo && <span className="sp-project-hull">#{String(p.newbuildingNo).padStart(3, '0')}</span>}
              </button>
              <button
                className="sp-project-info-btn"
                title="Open project info"
                onClick={() => { onSelectProject(p); onOpenProjectInfo(p); }}
              >
                <VscChevronRight size={14} />
              </button>
            </div>
          ))}
          {projects.length === 0 && (
            <div className="side-panel-empty">No projects yet.</div>
          )}
          {hiddenCount > 0 && (
            <button className="sp-hidden-note" onClick={() => setChoosingProjects(true)}>
              {hiddenCount} hidden project{hiddenCount === 1 ? '' : 's'} — choose…
            </button>
          )}
        </nav>
        )}
      </div>
    );
  }

  if (activeSection === 'lists') {
    return (
      <div className="side-panel">
        <div className="side-panel-header">
          <span className="side-panel-title">LISTS</span>
        </div>
        {!activeProject ? (
          <div className="side-panel-empty">Select a project first.</div>
        ) : (
          <nav className="sp-nav">
            <NavItem id="load-list"      label="Items"  Icon={IoFlashOutline} color="#4ec9b0"
              active={activeTabType === 'load-list'}      onClick={handleClick} onDoubleClick={handleDblClick} />
            <NavItem id="cable-register" label="Cables" Icon={MdCable}        color="#ce9178"
              active={activeTabType === 'cable-register'} onClick={handleClick} onDoubleClick={handleDblClick} />

            {role !== 'client' && (
              <button className="sp-group-header" onClick={() => setLookupsOpen(o => !o)}>
                <VscChevronRight size={12} className={`sp-group-chevron${lookupsOpen ? ' open' : ''}`} />
                Reference Tables
              </button>
            )}

            {lookupsOpen && role !== 'client' && LOOKUP_NAV.map(({ id, label }) => (
              <NavItem key={id} id={id} label={label} Icon={VscDatabase} color="#9b8ec4"
                active={activeTabType === id} onClick={handleClick} onDoubleClick={handleDblClick} indent />
            ))}

            <button className="sp-group-header" onClick={() => setReportsOpen(o => !o)}>
              <VscChevronRight size={12} className={`sp-group-chevron${reportsOpen ? ' open' : ''}`} />
              Reports
            </button>

            {reportsOpen && REPORT_NAV.map(({ id, label }) => (
              <NavItem key={id} id={id} label={label} Icon={VscChecklist} color="#d19a66"
                active={activeTabType === id} onClick={handleClick} onDoubleClick={handleDblClick} indent />
            ))}
          </nav>
        )}
      </div>
    );
  }

  if (activeSection === 'files') {
    if (!activeProject) {
      return (
        <div className="side-panel">
          <div className="side-panel-header"><span className="side-panel-title">FILES</span></div>
          <div className="side-panel-empty">Select a project first.</div>
        </div>
      );
    }
    return (
      <FileSidebar
        project={activeProject}
        role={role}
        activeTabId={activeTabId}
        onOpenFolder={(folder) => onOpenTab(`files-folder-${folder._id}`, folder.name, { type: 'files-folder', folder })}
        onPreviewFolder={(folder) => onPreviewTab?.(`files-folder-${folder._id}`, folder.name, { type: 'files-folder', folder })}
        onOpenPersonalFolder={(folder) => onOpenTab(`personal-folder-${folder._id}`, folder.name, { type: 'personal-folder', folder })}
        onPreviewPersonalFolder={(folder) => onPreviewTab?.(`personal-folder-${folder._id}`, folder.name, { type: 'personal-folder', folder })}
      />
    );
  }

  if (activeSection === 'planning') {
    const canMyCalendar = ['admin','engineer','manager','foreman'].includes(role);
    return (
      <div className="side-panel">
        <div className="side-panel-header"><span className="side-panel-title">PLANNING</span></div>
        <nav className="sp-nav" style={{ flex: 'none' }}>
          {canMyCalendar && (
            <NavItem id="my-calendar" label="My Calendar" Icon={IoCalendarOutline} color="#c586c0"
              active={activeTabId === 'my-calendar'} onClick={handleClick} onDoubleClick={handleDblClick} />
          )}
          {activeProject && (
            <NavItem id="project-calendar" label="Project Calendar" Icon={IoCalendarOutline} color="#4fc1ff"
              active={activeTabId === `project-calendar-${activeProject._id}`} onClick={handleClick} onDoubleClick={handleDblClick} />
          )}
        </nav>
        <div className="sp-subheader">Gantt diagrams</div>
        {!activeProject ? (
          <div className="side-panel-empty">Select a project first.</div>
        ) : (
          <PlanningSidebar
            project={activeProject}
            activeTabId={activeTabId}
            role={role}
            onOpenDiagram={d => onOpenTab(`gantt-${d._id}`, d.name, { type: 'gantt-diagram', diagram: d })}
          />
        )}
      </div>
    );
  }

  if (activeSection === 'quotes') {
    return (
      <div className="side-panel">
        <div className="side-panel-header"><span className="side-panel-title">QUOTES</span></div>
        {!activeProject ? (
          <div className="side-panel-empty">Select a project first.</div>
        ) : (
          <QuoteSidebar
            project={activeProject}
            activeTabId={activeTabId}
            onOpenQuote={q => onOpenTab(`quote-${q._id}`, q.name, { type: 'quote', quoteId: q._id, project: activeProject })}
            onOpenSummary={() => onOpenTab('quote-summary', 'Quote Summary')}
          />
        )}
      </div>
    );
  }

  if (activeSection === 'job-packages') {
    const isPrivileged = ['admin','engineer','manager','foreman'].includes(role);
    return (
      <div className="side-panel">
        <div className="side-panel-header"><span className="side-panel-title">JOB PACKAGES</span></div>
        {!isPrivileged ? (
          <nav className="sp-nav">
            <NavItem id="my-job-packages" label="My Job Packages"
              active={activeTabType === 'my-job-packages'}
              onClick={handleClick} onDoubleClick={handleDblClick} />
          </nav>
        ) : !activeProject ? (
          <div className="side-panel-empty">Select a project first.</div>
        ) : (
          <nav className="sp-nav">
            <NavItem id="job-packages" label="Job Packages"
              active={activeTabType === 'job-packages'}
              onClick={handleClick} onDoubleClick={handleDblClick} />
          </nav>
        )}
      </div>
    );
  }

  if (activeSection === 'feedback') {
    return (
      <div className="side-panel">
        <div className="side-panel-header"><span className="side-panel-title">FEEDBACK</span></div>
        <nav className="sp-nav">
          <NavItem id="feedback" label="Feedback & Bugs"
            active={activeTabType === 'feedback'}
            onClick={handleClick} onDoubleClick={handleDblClick} />
        </nav>
      </div>
    );
  }

  if (activeSection === 'chat') {
    return (
      <div className="side-panel">
        <div className="side-panel-header"><span className="side-panel-title">CHAT</span></div>
        <ChatSidebar
          socket={socket}
          currentUser={currentUser}
          groups={chatGroups}
          setGroups={setChatGroups}
          activeGroupId={activeChatGroupId}
          onOpenChat={onOpenChat}
          onlineUsers={onlineUsers}
        />
      </div>
    );
  }

  if (activeSection === 'users') {
    return (
      <div className="side-panel">
        <div className="side-panel-header">
          <span className="side-panel-title">USERS</span>
        </div>
        <nav className="sp-nav">
          <NavItem id="users" label="Manage Users" active={activeTabType === 'users'}
            onClick={handleClick} onDoubleClick={handleDblClick} />
          <NavItem id="employee-overview" label="Employee Overview" active={activeTabId === 'employee-overview'}
            onClick={handleClick} onDoubleClick={handleDblClick} />
        </nav>
      </div>
    );
  }

  return (
    <div className="side-panel">
      <div className="side-panel-header">
        <span className="side-panel-title">{activeSection.toUpperCase()}</span>
      </div>
    </div>
  );
}

export default SidePanel;
